// Client of /api/judge. The server only sees "Player 1 … Player N"; the real
// names go in here, after the answer (like the app's _substituteNames).

import type { AuctionEngine } from './engine';

export interface Verdict {
  winner: number; // 0-based
  scores: number[];
  verdict: string;
}

export type JudgeError = 'rate_limited' | 'quota' | 'failed';

/// "Player 2" and the words models use for it in other languages, longest
/// index first so "Player 10" is not clobbered by "Player 1".
const PLAYER_WORDS = [
  'Player', 'Spieler', 'Spielerin', 'Joueur', 'Joueuse', 'Jugador', 'Jugadora', 'Giocatore', 'Giocatrice',
  'Jogador', 'Jogadora', 'Speler', 'Gracz', 'Oyuncu', 'Spiller', 'Spelare', 'Hráč', 'Játékos', 'Jucător',
  'Игрок', 'Гравець', 'Pemain', 'Người chơi', 'Παίκτης', 'खिलाड़ी', 'খেলোয়াড়', 'プレイヤー', '플레이어', '玩家', 'لاعب',
];

export function substituteNames(text: string, names: string[]): string {
  let out = text;
  const words = PLAYER_WORDS.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  for (let i = names.length - 1; i >= 0; i--) {
    out = out.replace(new RegExp(`(?:${words})\\s*${i + 1}(?!\\d)`, 'giu'), names[i]);
  }
  return out;
}

export async function requestVerdict(
  e: AuctionEngine,
  opts: { theme: string; base?: string; language: string; names: string[]; pid: string },
): Promise<Verdict> {
  const res = await fetch('/api/judge', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      theme: opts.theme,
      base: opts.base ?? null,
      builds: e.builds.map((b) => b.map((w) => ({ name: w.name, price: w.price }))),
      wallets: e.wallets,
      language: opts.language,
      pid: opts.pid,
    }),
  });
  if (res.status === 429) throw 'rate_limited' satisfies JudgeError;
  if (res.status === 402) throw 'quota' satisfies JudgeError;
  if (!res.ok) throw 'failed' satisfies JudgeError;
  const j = (await res.json()) as { winner: number; scores: number[]; verdict: string };
  return { winner: j.winner - 1, scores: j.scores, verdict: substituteNames(j.verdict, opts.names) };
}
